import React, { useEffect, useState } from "react";
import { TbEye, TbEyeOff, TbMail } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import LoginImg from "../../assets/image/login-bg.jpeg";
import Logo from "../../assets/svgs/logo";
import { useAuth } from "../../helper/authService";
import {
  Box,
  Button,
  Checkbox,
  Col,
  FabButton,
  Flex,
  Gap,
  InputField,
  Link,
  Row,
  Typography,
} from "../../ppui";
import { theme } from "../../theme";
// import useAPIHandler from "../../hook/use-api-handler";
import { useAuthContext } from "../../context/authContext";
import { removeUnderScrSpaceBet, sliceText } from "../../utils/common";
import { loginSchema } from "./login-validation";
import { Brand, FooterBox, FromBox, ImageBox } from "../registration/style";

// Styled Components
const ScreenLayout = styled.section({
  position: "fixed",
  height: "100%",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "1.25rem",
  gap: "4.813rem",
  backgroundColor: theme.colors.light.light,
});

const ContentWrapper = styled(Flex)({
  height: "100%",
  width: "55%",
  flexGrow: 1,
  overflow: "hidden",
  flexDirection: "column",
  justifyContent: "space-between",
});

const Gap30 = styled(Box)({
  height: "1.875rem",
});

const Gap20 = styled(Box)({
  height: "1.25rem",
});

function Login() {
  const { getOrganizationFn, setSigninEmail } = useAuthContext();
  const navigate = useNavigate();

  const { login, loading } = useAuth();
  // const { fetchData } = useAPIHandler();

  // State management
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: localStorage.getItem("hrmsRemMe") === "true",
  });

  const [formErrors, setFormErrors] = useState({
    email: "",
    password: "",
    general: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [getProfileLoading, setGetProfileLoading] = useState(false);

  // Load saved credentials
  useEffect(() => {
    if (localStorage.getItem("hrmsRemMe") === "true") {
      const savedEmail = localStorage.getItem("savedEmail");
      const savedPassword = localStorage.getItem("savedPassword");

      if (savedEmail && savedPassword) {
        setFormData((prev) => ({
          ...prev,
          email: savedEmail,
          password: savedPassword,
        }));
      }
    }
  }, []);

  function handleBlurFn(e, name) {
    let value = e;

    if (e && e.target) {
      value = e.target.value;
    }

    const fieldSchema = loginSchema.extract(name);
    const { error } = fieldSchema.validate(value);

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error ? error.details[0].message : undefined,
      general: "",
    }));
  }
  // Form handlers
  const handleInputChange = (e, name) => {
    let value = e;

    if (e && e.target) {
      value = e.target.value;
    }
    if (name === "email") {
      value = sliceText(removeUnderScrSpaceBet(value.toLowerCase()), 150);
    }
    // const finalValue =  sliceText(removeUnderScrSpaceBet(value),150);

    setFormData((prev) => ({ ...prev, [name]: value }));

    const fieldSchema = loginSchema.extract(name);
    const { error } = fieldSchema.validate(value);

    if (!error) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: error ? error.details[0].message : undefined,

        general: "",
      }));
    }
  };

  const handlePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { email, password } = formData;

    const { error } = loginSchema.validate(
      {
        email,
        password,
      },
      {
        abortEarly: false,
      }
    );

    let validationErrors = {};

    if (error && error?.details?.length > 0) {
      validationErrors = error.details.reduce((acc, err) => {
        const pathname = err.path.join(".");
        acc[pathname] = err.message;
        return acc;
      }, {});

      if (Object.keys(validationErrors).length > 0) {
        setFormErrors({
          ...validationErrors,
          general: "",
        });

        return;
      } else {
        setFormErrors({
          email: "",
          password: "",
          general: "",
        });
      }
    }

    setGetProfileLoading(true);

    try {
      const response = await login(formData.email, formData.password);
      const token = response?.data?.access_token;

      if (token) {
        setSigninEmail(response?.data?.email)
        localStorage.setItem("hrms_token", token);

        // Handle remember me
        if (formData.rememberMe) {
          localStorage.setItem("savedEmail", formData.email);
          localStorage.setItem("savedPassword", formData.password);
          localStorage.setItem("hrmsRemMe", "true");
        } else {
          localStorage.removeItem("savedEmail");
          localStorage.removeItem("savedPassword");
          localStorage.removeItem("hrmsRemMe");
        }

        const orgResponse = await getOrganizationFn({ customToken: token });

        // Navigation handling
        const targetPath = orgResponse?.data?.org_name
          ? "/dashboard"
          : "/onboarding";
        window.history.pushState(null, "", targetPath);
        navigate(targetPath, { replace: true });
      }
    } catch (error) {
      if (error?.type === "GeneralError") {
        setFormErrors((prev) => ({
          ...prev,
          general: error.message,
        }));
      }

      error?.errors?.forEach((err) => {
        if (err?.field === "email" || err?.field === "password") {
          setFormErrors((prev) => ({
            ...prev,
            [err.field]: err.message,
          }));
        }
      });
    } finally {
      setGetProfileLoading(false);
    }
  };

  return (
    <ScreenLayout>
      <ImageBox>
        <img src={LoginImg} alt="Login background" />
      </ImageBox>
      <ContentWrapper>
        <Brand>
          <Logo />
        </Brand>
        <FromBox>
          <Typography variant="h3" mb="10">
            Login
          </Typography>
          <Typography variant="body2" color="dark" colorVariant="light">
            Welcome back! Please log in to your account to continue.
          </Typography>
          <Gap30 />
          <form onSubmit={handleSubmit} noValidate>
            <Box pb={22}>
              <InputField
                name="email"
                label="Enter Your Email"
                variant="standard"
                required={true}
                placeholder="Email"
                fullWidth
                labelBold
                prefix={<TbMail />}
                value={formData.email}
                onChange={(e) => handleInputChange(e, "email")}
                helperText={formErrors.email}
                error={formErrors.email}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = e.clipboardData.getData("text").trim();
                  setFormData((prev) => ({
                    ...prev,
                    email: text,
                  }));
                }}
                onBlur={(e) => handleBlurFn(e, "email")}
              />
            </Box>

            <Box pb={22}>
              <InputField
                type={showPassword ? "text" : "password"}
                name="password"
                label="Enter Your Password"
                variant="standard"
                required={true}
                placeholder="Password"
                fullWidth
                labelBold
                onBlur={(e) => handleBlurFn(e, "password")}
                prefix={
                  <FabButton
                    icon={showPassword ? <TbEye /> : <TbEyeOff />}
                    onClick={handlePasswordVisibility}
                    type="button"
                    color="default"
                  />
                }
                value={formData.password}
                onChange={(e) => handleInputChange(e, "password")}
                helperText={formErrors.password}
                error={formErrors.password}
              />
            </Box>

            <Flex alignItems="center" justifyContent="space-between">
              <Checkbox
                name="rememberMe"
                color="primary"
                label="Remember me"
                checked={formData.rememberMe}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    rememberMe: !formData.rememberMe,
                  }))
                }
              />
              {/* <Link
                color="dark"
                colorVariant="light"
                underline="always"
                to="/forgot-password"
              >
                Forgot Password?
              </Link> */}
            </Flex>

            <Gap20 />
            {formErrors.general && (
              <Typography variant="body2" color="danger">
                {formErrors.general}
              </Typography>
            )}

            <Row>
              <Col md={4}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  block
                  fullWidth
                  loading={getProfileLoading || loading}
                  disabled={loading || !!formErrors.email}
                >
                  Login
                </Button>
              </Col>
            </Row>
            <Gap height="20" />
            <Typography align="left" variant="body2" mt="2">
              Don&#39;t have an account?
              <Link to="/registration"> Sign Up</Link>
            </Typography>
          </form>
        </FromBox>
        <FooterBox>
          {/* <Typography variant="body2" color="dark" colorVariant="light">
            For more detailed information please review our{" "}
            <Link color="dark" underline="always" to="/">
              Privacy Policy
            </Link>
            . Need help?{" "}
            <Link color="dark" underline="always" to="/">
              Contact us
            </Link>
          </Typography> */}
        </FooterBox>
      </ContentWrapper>
    </ScreenLayout>
  );
}

export default Login;
